import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';

import TextField from '@mui/material/TextField';
import Fab from '@mui/material/Fab';
import { Box, Divider, Typography, Grid, Button, Container, Paper, CircularProgress, Chip, FormControl, Autocomplete } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import CustomChip from '../../utils/components/CustomChip';
import CustomMessage from '../../utils/components/CustomMessage';
import { formatMoney } from '../../utils/functions/Utils';

import { styled } from '@mui/material/styles';

import FormatoFactura from '../formatos/FormatoFactura';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const EditarFactura = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [factura, setFactura] = useState({ noFactura: '', importeLetra: '', serie: '', folio: '', estatus: 'Pendiente', cfdi: '' });
    const [emisor, setEmisor] = useState({ administradorUnico: '' });
    const [receptor, setReceptor] = useState({ denominacion: '', rfc: '', direccion: '' });
    const [cfdi, setCfdi] = useState({});
    const [verifySuccess, setVerifySuccess] = useState(<></>);
    const [conceptosD, setConceptosD] = useState(null);
    const [reportesD, setReportesD] = useState(null);
    const [precioReporte, setPrecioReporte] = useState(null);
    const [errorFile, setErrorFile] = useState(true);
    const [fileName, setFileName] = useState('');
    const [configs, setConfigs] = useState({});
    const [fechaPago, setFechaPago] = useState();

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' });

    const estatus = ['Pendiente por Pagar', 'Pagada', 'Cancelada'];

    // get id from url
    let { id } = useParams()

    useEffect(() => {
        if (auth.user.rol !== "Admin") {
            navigate('/dashboard');
        }
        document.title = 'Editar Factura';
    }, [auth])

    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    useEffect(() => {
        getData();
    }, [])

    const getData = useCallback(async () => {
        try {
            let res = await axios.get(`/api/configs/company/${auth.user.company}`);
            if (res.status === 200) {
                setConfigs(res.data);
                setEmisor(state => {
                    return {
                        ...state,
                        denominacion: res.data.datosFiscales.denominacion,
                        regimenFiscal: res.data.datosFiscales.regimenFiscal,
                        rfc: res.data.datosFiscales.rfc,
                        direccion: res.data.datosFiscales.direccion,
                        telefono: res.data.datosFiscales.telefono,
                        administradorUnico: res.data.datosFiscales.administradorUnico
                    }
                });
            }
            res = await axios.get(`/api/facturas/${id}`);
            if (res.status === 200) {
                setFactura(res.data);
                setEmisor(res.data.emisor);
                setReceptor(res.data.receptor);
                setCfdi(res.data.cfdi);
                res.data.conceptos ? setConceptosD(res.data.conceptos) : setConceptosD(null);
                res.data.cotizaciones ? setReportesD(res.data.cotizaciones) : setReportesD(null);
                parseCfdi2(res.data.cfdi);
                setFileName(res.data.fileName);
                res.data.fechaPago ? setFechaPago(DateTime.fromISO(res.data.fechaPago)) : setFechaPago();
            }
        } catch (e) {
            console.log(e);
        }
    }, [])

    const parseCfdi = async () => {
        if (factura.cfdi !== '') {
            setVerifySuccess(<CircularProgress size={20} />);
            try {
                const res = await axios.post('/api/facturas/parseCfdi', { "cfdi": factura.cfdi })
                if (res.status === 200) {
                    setEmisor(state => {
                        return {
                            ...state,
                            denominacion: res.data.cfdi2.emisor.Nombre,
                            rfc: res.data.cfdi2.emisor.Rfc
                        }
                    });
                    setReceptor(state => {
                        return {
                            ...state,
                            denominacion: res.data.cfdi2.receptor.Nombre,
                            rfc: res.data.cfdi2.receptor.Rfc
                        }
                    });
                    let sf = {};
                    sf.folio = res.data.cfdi2.comprobante.Folio ? res.data.cfdi2.comprobante.Folio : "";
                    sf.serie = res.data.cfdi2.comprobante.Serie ? res.data.cfdi2.comprobante.Serie : "J";
                    sf.noFactura = sf.serie + sf.folio;
                    setFactura(state => {
                        return {
                            ...state,
                            noFactura: sf.noFactura,
                            folio: sf.folio,
                            serie: sf.serie,
                            subTotal: res.data.cfdi2.comprobante.SubTotal,
                            iva: res.data.cfdi2.totalImpuestos.TotalImpuestosTrasladados,
                            total: res.data.cfdi2.comprobante.Total,
                            fechaEmision: res.data.cfdi2.comprobante.Fecha,
                        }
                    })
                    setCfdi(res.data.cfdi2);
                    let tmp = {};
                    res.data.cfdi2.conceptos.forEach((c, i) => {
                        tmp[i] = c['$'].Descripcion;
                    })
                    setConceptosD(tmp);
                    tmp = {};
                    res.data.cfdi2.conceptos.forEach((c, i) => {
                        tmp[i] = '';
                    })
                    setReportesD(tmp);
                    tmp = {};
                    res.data.cfdi2.conceptos.forEach((c, i) => {
                        tmp[i] = c['$'].Descripcion;
                    })
                    setPrecioReporte(tmp);
                    setVerifySuccess(<i className='fad fa-check-circle' />);
                }
            } catch (e) {
                setVerifySuccess(<i className='fad fa-exclamation-triangle' />);
                console.log(e);
                setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error con los datos ingresados, favor de revisar los datos e intentarlo de nuevo', severity: 'error' } });
            }
        } else {
            setApiRes(state => { return { ...state, show: true, message: 'No se ha seleccionado ningun archivo', severity: 'error' } });
        }
    }

    const parseCfdi2 = async (cdfi) => {
        if (cdfi !== '') {
            setVerifySuccess(<CircularProgress size={20} />);
            try {
                const res = await axios.post('/api/facturas/parseCfdi', { "cfdi": cdfi })
                if (res.status === 200) {
                    setEmisor(state => {
                        return {
                            ...state,
                            denominacion: res.data.cfdi2.emisor.Nombre,
                            rfc: res.data.cfdi2.emisor.Rfc
                        }
                    });
                    setReceptor(state => {
                        return {
                            ...state,
                            denominacion: res.data.cfdi2.receptor.Nombre,
                            rfc: res.data.cfdi2.receptor.Rfc
                        }
                    });
                    setFactura(state => {
                        return {
                            ...state,
                            subTotal: res.data.cfdi2.comprobante.SubTotal,
                            iva: res.data.cfdi2.totalImpuestos.TotalImpuestosTrasladados,
                            total: res.data.cfdi2.comprobante.Total,
                            fechaEmision: res.data.cfdi2.comprobante.Fecha,
                        }
                    })
                    setCfdi(res.data.cfdi2);
                    setConceptosD(state => { return { ...state } });
                    setVerifySuccess(<i className='fad fa-check-circle' />);
                }
            } catch (e) {
                setVerifySuccess(<i className='fad fa-exclamation-triangle' />);
                console.log(e);
                setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error con los datos ingresados, favor de revisar los datos e intentarlo de nuevo', severity: 'error' } });
            }
        } else {
            setApiRes(state => { return { ...state, show: true, message: 'No se ha seleccionado ningun archivo', severity: 'error' } });
        }
    }

    const handleFile = (e) => {
        let file = e.target.files[0];
        try {
            let reader = new FileReader();
            setErrorFile(true);
            setFileName(file.name);
            if (file.type !== 'text/xml')
                return setApiRes(state => { return { ...state, show: true, message: 'El archivo seleccionado no es un archivo .xml', severity: 'error' } });

            reader.readAsText(file, 'UTF-8');
            reader.onloadend = () => {
                if (reader.readyState === 2) {
                    if (Number(file.size) > 26214400) {
                        // this.setState({fileMsg: "El archivo excede los 25 Mb, intenta con otro.", fileError: true})
                    } else {
                        setErrorFile(false);
                        setFactura(state => { return { ...state, cfdi: reader.result } })
                    }
                }
            }
        } catch (err) {
            console.log(err);
        }
    }

    const handleSaveFactura = async () => {
        try {
            let f = factura;
            f.conceptos = conceptosD;
            f.cotizaciones = reportesD;
            f.emisor = emisor;
            f.receptor = receptor;
            f.fechaPago = fechaPago;
            f.metodoPago = cfdi.comprobante.MetodoPago;
            const res = await axios.post(`/api/facturas/${id}`, { 'factura': f })
            if (res.status === 200) {
                setApiRes(state => { return { ...state, show: true, message: 'Factura actualizada con exito', severity: 'success' } });
            }
        } catch (e) {
            console.log(e);
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al actualizar la factura, favor de intentarlo de nuevo', severity: 'error' } });
        }
    }

    const verifyReports = async () => {
        try {
            for (let x of Object.keys(reportesD)) {
                const res = await axios.get(`/api/reports/getReportByNumber/?reporte=${reportesD[x].split(',')[0]}`);
                if (res.status === 200) {
                    const desc = `${res.data.reportes[0].equipo} MARCA ${res.data.reportes[0].marca} MODELO ${res.data.reportes[0].modelo} SERIE ${res.data.reportes[0].noSerie} INVENTARIO ${res.data.reportes[0].noInventario}`.toUpperCase()
                    setConceptosD(state => { return { ...state, [x]: desc } })
                    setPrecioReporte(state => { return { ...state, [x]: res.data.reportes[0].cotizacion.subtotal } })
                }
            }
        } catch (e) {
            console.log(e);
        }
    }

    const renderDescription = () => {
        if (cfdi.comprobante && conceptosD !== null && reportesD !== null) {
            return (
                <Box className='px-3'>
                    <Divider sx={{ mt: 2, mb: 5 }} textAlign="center">
                        <CustomChip variant='outlined' estatus='AUTORIZADO' label='Detalle del CFDI' className='text-bold' />
                    </Divider>
                    {/* <Typography sx={{mb: '12px'}}>Conceptos encontrados:</Typography> */}
                    <Box sx={{ display: 'flex' }}>
                        <p className='text-bold text-center' style={{ width: '100px', fontSize: '16px' }}>Cantidad</p>
                        <p className='text-bold text-center' style={{ width: '500px', fontSize: '16px' }}>Descripción</p>
                        <p className='text-bold text-center' style={{ width: '200px', fontSize: '16px' }}>Precio Unitario</p>
                        <p className='text-bold text-center' style={{ width: '200px', fontSize: '16px' }}># Reporte</p>
                        {precioReporte !== null ? <p className='text-bold text-center' style={{ width: '200px', fontSize: '16px' }}>Subtotal Cotizado</p> : <></>}
                    </Box>
                    {cfdi.conceptos.map((c, i) => {
                        return (
                            <div className='d-flex my-2 align-items-center' key={cfdi.comprobante.Sello.substring(i, i + 5)}>
                                <p className='text-bold text-center mb-0' style={{ width: '100px', fontSize: '16px' }}>{c['$'].Cantidad}</p>
                                <TextField label='...' value={conceptosD[i]} multiline onChange={(e) => setConceptosD({ ...conceptosD, [i]: e.target.value })} style={{ width: '500px' }} />
                                <p className='text-bold text-center mb-0' style={{ width: '200px', fontSize: '16px' }}>$ {formatMoney(c['$'].Importe)}</p>
                                <TextField label='...' size='small' value={reportesD[i]} onChange={(e) => setReportesD({ ...reportesD, [i]: e.target.value })} style={{ width: '200px' }} />
                                {precioReporte !== null ?
                                    <p className='text-bold text-center mb-0' style={{ width: '200px', fontSize: '16px', color: `${formatMoney(precioReporte[i]) === formatMoney(c['$'].Importe) ? '#000' : '#FF0000'}` }}>$ {formatMoney(precioReporte[i])}</p> :
                                    <></>
                                }
                            </div>
                        )
                    })}
                    <div className='text-center my-3'>
                        <Button variant='contained' color='secondary' className='px-3 py-2' onClick={() => verifyReports()}>Verificar descripcion y precio</Button>
                    </div>
                </Box>
            )
        }
    }

    useEffect(() => {
        if (cfdi.comprobante) {
            const tmp = [];
            cfdi.conceptos.forEach((c, i) => {
                tmp.push(c);
                tmp[i]['$'].Descripcion = conceptosD[i];
            })
            setCfdi(state => { return { ...state, conceptos: tmp } });
        }
    }, [conceptosD])

    useEffect(() => {
        setFactura({ ...factura, noFactura: `${factura.serie}${factura.folio}` })
    }, [factura.folio, factura.serie])

    const handleDownload = () => {
        // Crear el contenido del archivo
        const fileContent = factura.cfdi;

        // Crear un blob con el contenido
        const blob = new Blob([fileContent], { type: 'text/xml' });

        // Crear una URL para el blob
        const fileURL = window.URL.createObjectURL(blob);

        // Crear un enlace temporal
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = (`${fileName}.xml`).replace('.xml.xml', '.xml');  // Nombre del archivo

        // Hacer clic en el enlace para iniciar la descarga
        link.click();

        // Liberar el objeto URL
        window.URL.revokeObjectURL(fileURL);
    };

    return (
        <>
            <Container maxWidth='xxl' style={{ marginTop: '10px' }}>

                <Paper sx={{ padding: '24px 0px' }} elevation={3}>

                    <Container maxWidth='xl'>
                        <Box className='text-center' >
                            <Typography variant='h5' style={{ fontWeight: 700 }}>Formato para Factura</Typography>
                        </Box>

                        <Fab aria-label="add" onClick={() => handleSaveFactura()} style={{ position: 'fixed', top: '60px', left: '12px', zIndex: '10', backgroundColor: '#66bb6a', color: '#FFF' }}>
                            <i className="fad fa-save fa-2x"></i>
                        </Fab>

                        <Divider className='my-3' textAlign="left">
                            <CustomChip variant='outlined' estatus='AUTORIZADO' label='CDFI' className='text-bold' />
                        </Divider>

                        <Paper elevation={3} style={{ marginTop: '32px', borderRadius: '20px', padding: '20px 12px' }}>
                            <Grid container spacing={1} className='px-2'>
                                <Grid item xs={12} sm={7} md={7} lg={7}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            component="label"
                                            role={undefined}
                                            variant="contained"
                                            tabIndex={-1}
                                            startIcon={<CloudUploadIcon />}
                                        >
                                            Cargar CFDI
                                            <VisuallyHiddenInput type="file" onChange={(e) => handleFile(e)} />
                                        </Button>
                                        <Typography sx={{ px: 3, my: 0 }}>{fileName}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={2} md={2} lg={2} style={{ display: 'flex', alignItems: 'end' }} className='mb-2'>
                                    <Button disabled={errorFile} variant='contained' color='primary' className='px-3' onClick={() => parseCfdi()}>Verificar CFDI &nbsp;{verifySuccess}</Button>
                                </Grid>
                                <Grid item xs={12} sm={3} md={3} lg={3} style={{ display: 'flex', alignItems: 'end' }} className='mb-2'>
                                    <Button variant='contained' color='info' className='px-3' onClick={handleDownload} endIcon={<DownloadIcon />}>Descargar CFDI</Button>
                                </Grid>
                                <Grid item xs={12} >
                                    {renderDescription()}
                                </Grid>
                            </Grid>
                        </Paper>

                        <Divider className='my-3' textAlign="left">
                            <CustomChip variant='outlined' estatus='AUTORIZADO' label='Datos generales' className='text-bold' />
                        </Divider>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    onChange={(e) => setFactura({ ...factura, serie: e.target.value })}
                                    value={factura.serie}
                                    label="Serie"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    onChange={(e) => setFactura({ ...factura, folio: e.target.value })}
                                    value={factura.folio}
                                    label="Folio"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    onChange={(e) => setFactura({ ...factura, noFactura: e.target.value })}
                                    value={factura.noFactura}
                                    label="No. Factura"
                                    fullWidth
                                />
                            </Grid>
                            {/* <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    onChange={(e) => setEmisor({ ...emisor, administradorUnico: e.target.value })}
                                    value={emisor.administradorUnico}
                                    label="Administrador único"
                                    fullWidth
                                />
                            </Grid> */}
                            <Grid item xs={12} sm={6} md={6}>
                                <TextField
                                    onChange={(e) => setFactura({ ...factura, importeLetra: e.target.value })}
                                    value={factura.importeLetra}
                                    label="Importe con letra"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FormControl style={{ display: 'flex' }}>
                                    <DatePicker
                                        label="Fecha de Pago"
                                        format="dd/MM/yyyy"
                                        value={fechaPago}
                                        onChange={(newValue) => setFechaPago(newValue)}
                                        slotProps={{ textField: { variant: 'outlined' } }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    value={cfdi.comprobante ? cfdi.comprobante.MetodoPago : ''}
                                    label="Método de Pago"
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl required style={{ display: 'flex' }}>
                                    <Autocomplete
                                        disablePortal
                                        freeSolo={true}
                                        onInputChange={(event, newInputValue) => setFactura({ ...factura, estatus: newInputValue })}
                                        inputValue={factura.estatus}
                                        value={factura.estatus}
                                        defaultValue={factura.estatus}
                                        options={estatus}
                                        renderInput={(params) => <TextField {...params} label='Estatus' />}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Divider className='my-3' textAlign="left">
                            <CustomChip variant='outlined' estatus='AUTORIZADO' label='Datos del Receptor' className='text-bold' />
                        </Divider>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={receptor.denominacion}
                                    label="Denominación"
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    value={receptor.rfc}
                                    label="RFC"
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <TextField
                                    onChange={(e) => setReceptor({ ...receptor, direccion: e.target.value })}
                                    value={receptor.direccion}
                                    label="Dirección"
                                    multiline
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <div className='d-flex my-3'>
                            <Button variant='contained' color='success' onClick={() => handleSaveFactura()}>Guardar &nbsp;<i className='fad fa-save'></i></Button>
                        </div>
                        <br />

                    </Container>
                    <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
                </Paper>

                <Container maxWidth='xl'>
                    <Divider className='my-5' textAlign="center">
                        <Chip
                            label='Visualizador de Factura'
                            className='text-bold'
                            style={{ borderColor: '#ec407a', color: '#000' }}
                            variant='outlined'
                        />
                    </Divider>
                    <FormatoFactura cfdi={cfdi} emisor={emisor} receptor={receptor} factura={factura} configs={configs} />
                </Container>


            </Container>
        </>
    )
}

export default EditarFactura;